/**
 * Saving an app's web search policy from Apps & Surfaces. The PUT replaces the stored policy whole, and the status the
 * screen shows has maxUses already resolved through the Usage caps default; sending that back pinned the app to whatever
 * the default was at that moment (2026-10-06 review of #303). The body is built from the stored fields instead.
 */
import type { WebSearchPolicyPatch, WebSearchPolicyStatus } from './api';

/** The stored policy with `change` applied. maxUses null (or no own value) follows the Usage caps default. */
export function webSearchPatch(status: WebSearchPolicyStatus | undefined, change: { enabled?: boolean; maxUses?: number | null }): WebSearchPolicyPatch {
  return {
    enabled: change.enabled ?? status?.enabled ?? true,
    maxUses: change.maxUses !== undefined ? change.maxUses : status?.ownMaxUses ?? null,
    ...(status?.allowedDomains?.length ? { allowedDomains: status.allowedDomains } : {}),
    ...(status?.blockedDomains?.length ? { blockedDomains: status.blockedDomains } : {}),
    ...(status?.userLocation ? { userLocation: status.userLocation } : {}),
  };
}

/**
 * The searches-per-request field's draft, read when the person leaves the field or presses Enter: a whole number from 1
 * to 30 to save, or undefined to save nothing (blank or mistyped). Clearing the field never drops the app's own value;
 * following the default again is the 'use default' button.
 */
export function parseMaxUsesDraft(draft: string): number | undefined {
  const text = draft.trim();
  if (!/^\d+$/.test(text)) return undefined;
  const n = Number(text);
  return n >= 1 && n <= 30 ? n : undefined;
}
